/**
 * Helpers for building Cube queries from the report builder form, shaping
 * Cube results for display, and reasoning about which cubes can be combined.
 * Pure functions: no React, no network.
 */

export const DATE_RANGES = [
  { value: '', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this week', label: 'This week' },
  { value: 'this month', label: 'This month' },
  { value: 'this quarter', label: 'This quarter' },
  { value: 'this year', label: 'This year' },
  { value: 'last 7 days', label: 'Last 7 days' },
  { value: 'last 30 days', label: 'Last 30 days' },
  { value: 'last 90 days', label: 'Last 90 days' },
  // Complete months: the current month is not included, which the label says.
  { value: 'last 6 months', label: 'Last 6 full months' },
  { value: 'last 12 months', label: 'Last 12 full months' },
  { value: 'last week', label: 'Last week' },
  { value: 'last month', label: 'Last month' },
  { value: 'last quarter', label: 'Last quarter' },
  { value: 'last year', label: 'Last year' },
];

/**
 * Financial-year periods. The API resolves these with the company's year
 * start, so they work for the dashboard period and statements but not inside
 * a Cube query a report stores.
 */
export const FINANCIAL_RANGES = [
  { value: 'financial year to date', label: 'Financial year to date' },
  { value: 'this financial year', label: 'This financial year' },
  { value: 'last financial year', label: 'Last financial year' },
  { value: 'last financial year to date', label: 'Last financial year to date' },
];

/**
 * Custom periods, written as text the API resolves to dates:
 *   "last 6 months"                       the six complete months before this one
 *   "last 6 months including this month"  this month so far and the five before it
 *   "since 2026-05-01"                    that day to today
 *   "2026-04-01 to 2026-09-30"            between two dates, inclusive
 */
export const PERIOD_UNITS = ['day', 'week', 'month', 'quarter', 'year'];
const CURRENT_UNIT = { day: 'today', week: 'this week', month: 'this month', quarter: 'this quarter', year: 'this year' };
const PRESETS = [...DATE_RANGES, ...FINANCIAL_RANGES];

export function parsePeriod(value) {
  const v = String(value || '').trim().toLowerCase();
  let m = /^last (\d+) (day|week|month|quarter|year)s?( including (?:today|this \w+))?$/.exec(v);
  if (m) return { kind: 'last', count: Number(m[1]), unit: m[2], current: !!m[3] };
  m = /^since (\d{4}-\d{2}-\d{2})$/.exec(v);
  if (m) return { kind: 'since', start: m[1] };
  m = /^(\d{4}-\d{2}-\d{2})\s*(?:to|-|,)\s*(\d{4}-\d{2}-\d{2})$/.exec(v);
  if (m) return { kind: 'between', start: m[1], end: m[2] };
  return null;
}

export function formatPeriod(p) {
  if (p.kind === 'last') {
    const base = `last ${p.count} ${p.unit}${p.count === 1 ? '' : 's'}`;
    return p.current ? `${base} including ${CURRENT_UNIT[p.unit]}` : base;
  }
  if (p.kind === 'since') return `since ${p.start}`;
  return `${p.start} to ${p.end}`;
}

/** Whether a period is one of the listed choices rather than a custom one. */
export const isPresetPeriod = (value) => !value || PRESETS.some((r) => r.value === value);

const dayLabel = (iso) => {
  const [y, mo, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[mo - 1] || '?'} ${y}`;
};

/** Human label for any period value ("this month" → "This month"). */
export function periodLabel(value) {
  if (!value) return 'All time';
  if (Array.isArray(value)) return `${dayLabel(value[0])} – ${dayLabel(value[1])}`;
  const known = PRESETS.find((r) => r.value === value)?.label;
  if (known) return known;
  const p = parsePeriod(value);
  if (p?.kind === 'last') {
    const units = `${p.count} ${p.unit}${p.count === 1 ? '' : 's'}`;
    if (p.current) return `Last ${units} including ${CURRENT_UNIT[p.unit]}`;
    // Without the current one they are complete: say so for the longer units.
    return p.unit === 'day' ? `Last ${units}` : `Last ${p.count} full ${p.unit}${p.count === 1 ? '' : 's'}`;
  }
  if (p?.kind === 'since') return `Since ${dayLabel(p.start)}`;
  if (p?.kind === 'between') return `${dayLabel(p.start)} – ${dayLabel(p.end)}`;
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

export const GRANULARITIES = [
  { value: '', label: 'No breakdown (filter only)' },
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
  { value: 'month', label: 'By month' },
  { value: 'quarter', label: 'By quarter' },
  { value: 'year', label: 'By year' },
];

export const FILTER_OPERATORS = [
  { value: 'equals', label: 'is', needsValue: true },
  { value: 'notEquals', label: 'is not', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'notContains', label: 'does not contain', needsValue: true },
  { value: 'gt', label: '>', needsValue: true },
  { value: 'gte', label: '>=', needsValue: true },
  { value: 'lt', label: '<', needsValue: true },
  { value: 'lte', label: '<=', needsValue: true },
  { value: 'set', label: 'is set', needsValue: false },
  { value: 'notSet', label: 'is not set', needsValue: false },
];

export const EMPTY_FORM = {
  title: '',
  displayType: 'table',
  measures: [],
  dimensions: [],
  timeDimension: '',
  granularity: '',
  dateRange: '',
  filters: [],
  orderMember: '',
  orderDir: 'desc',
  limit: 100,
  refreshMinutes: 30,
  compare: '',
  totalRow: false,
  topN: null,
  calculations: [],
  colorRules: [],
  orientation: 'vertical',
  stacking: 'auto',
  lineSeries: null,
  pieStyle: 'donut',
  pieSlices: 8,
};

export const cubeOf = (member) => (typeof member === 'string' ? member.split('.')[0] : '');

/** Build a Cube query object from the builder form state. */
export function buildCubeQuery(form) {
  const query = {
    measures: [...(form.measures || [])],
    dimensions: [...(form.dimensions || [])],
  };

  if (form.timeDimension) {
    const td = { dimension: form.timeDimension };
    if (form.granularity) td.granularity = form.granularity;
    if (form.dateRange) td.dateRange = form.dateRange;
    query.timeDimensions = [td];
  }

  const filters = (form.filters || [])
    .filter((f) => f.member && f.operator)
    .map((f) => {
      const op = FILTER_OPERATORS.find((o) => o.value === f.operator);
      const filter = { member: f.member, operator: f.operator };
      if (!op || op.needsValue) {
        // Arrays come from the report editor; comma-separated text from older forms.
        const raw = Array.isArray(f.values) ? f.values : String(f.values ?? '').split(',');
        filter.values = raw.map((v) => String(v).trim()).filter((v) => v !== '');
      }
      return filter;
    })
    .filter((f) => !f.values || f.values.length > 0);
  if (filters.length) query.filters = filters;

  if (form.orderMember) {
    query.order = { [form.orderMember]: form.orderDir === 'asc' ? 'asc' : 'desc' };
  }

  const limit = Number(form.limit);
  if (Number.isFinite(limit) && limit > 0) query.limit = Math.min(limit, 10000);

  return query;
}

/** Restore builder form state from a saved widget config. */
export function formFromConfig(config = {}) {
  const q = config.cubeQuery || {};
  const td = (q.timeDimensions || [])[0] || {};
  const orderEntry = q.order ? Object.entries(q.order)[0] : null;
  return {
    ...EMPTY_FORM,
    title: config.title || '',
    displayType: config.displayType || 'table',
    measures: q.measures || [],
    dimensions: q.dimensions || [],
    timeDimension: td.dimension || '',
    granularity: td.granularity || '',
    dateRange: typeof td.dateRange === 'string' ? td.dateRange : '',
    filters: (q.filters || []).map((f) => ({
      member: f.member || f.dimension || '',
      operator: f.operator || 'equals',
      values: Array.isArray(f.values) ? f.values.join(', ') : '',
    })),
    orderMember: orderEntry ? orderEntry[0] : '',
    orderDir: orderEntry ? orderEntry[1] : 'desc',
    limit: q.limit || EMPTY_FORM.limit,
    refreshMinutes: config.refreshMinutes || EMPTY_FORM.refreshMinutes,
  };
}

/** The cube a saved report is "about" (for the widget's dataset_name reference). */
export function primaryCube(form) {
  const first = (form.measures || [])[0] || (form.dimensions || [])[0] || form.timeDimension;
  return cubeOf(first);
}

/**
 * Cubes reachable (via declared joins, transitively) from a starting cube,
 * including itself. `cubes` is the meta list with { name, joins: [{ to }] }.
 */
export function reachableFrom(cubes, start) {
  const byName = new Map((cubes || []).map((c) => [c.name, c]));
  const seen = new Set();
  const stack = [start];
  while (stack.length) {
    const name = stack.pop();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    for (const j of byName.get(name)?.joins || []) stack.push(j.to);
  }
  return seen;
}

/**
 * Cubes whose dimensions may be combined with the selected measures: each
 * measure's cube must be able to reach them. With no measures, every cube.
 */
export function allowedDimensionCubes(cubes, measures) {
  const measureCubes = [...new Set((measures || []).map(cubeOf).filter(Boolean))];
  if (measureCubes.length === 0) return new Set((cubes || []).map((c) => c.name));
  let allowed = null;
  for (const mc of measureCubes) {
    const reach = reachableFrom(cubes, mc);
    allowed = allowed ? new Set([...allowed].filter((n) => reach.has(n))) : reach;
  }
  return allowed || new Set();
}

/**
 * Turn Cube result rows ({ 'tickets.team': 'Ops', 'tickets.count': '3' }) into
 * display rows keyed by readable titles, using the response annotation.
 * Numeric measures are converted to numbers for charts.
 */
const GRANULARITY_SET = new Set(['second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year']);
const ISO_TIME = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?)?/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Granularity suffix of a Cube result key such as `tickets.opened_at.month`, or null. */
export function granularityOf(key) {
  const last = String(key || '').split('.').pop();
  return GRANULARITY_SET.has(last) ? last : null;
}

/**
 * Human label for a Cube time value ("2026-04-01T00:00:00.000") at a
 * granularity: "Apr 2026", "Q2 2026", "w/c 06 Apr 2026", "01 Apr 2026".
 * Reads the date parts from the string so no timezone shift creeps in.
 */
export function formatTimeBucket(value, granularity = null) {
  if (value === null || value === undefined || value === '') return value;
  const text = String(value);
  if (!ISO_TIME.test(text)) return value;
  const [y, mo, d] = text.slice(0, 10).split('-').map(Number);
  const hm = text.slice(11, 16);
  const day = `${String(d).padStart(2, '0')} ${MONTHS[mo - 1]} ${y}`;
  switch (granularity) {
    case 'year': return String(y);
    case 'quarter': return `Q${Math.floor((mo - 1) / 3) + 1} ${y}`;
    case 'month': return `${MONTHS[mo - 1]} ${y}`;
    case 'week': return `w/c ${day}`;
    case 'day': return day;
    case 'hour':
    case 'minute': return `${day} ${hm}`;
    case 'second': return `${day} ${text.slice(11, 19)}`;
    default: return hm && hm !== '00:00' ? `${day} ${hm}` : day;
  }
}

/** Keys of a result that hold time values, mapped to their granularity (or null). */
export function timeKeysOf(annotation = {}) {
  const keys = new Map();
  for (const [key, meta] of Object.entries(annotation.timeDimensions || {})) keys.set(key, granularityOf(key) || meta.granularity || null);
  for (const [key, meta] of Object.entries(annotation.dimensions || {})) if (meta.type === 'time') keys.set(key, granularityOf(key));
  return keys;
}

/**
 * Column label for each member in a result: its title, or the member key
 * when two members share a title.
 */
export function displayLabels(annotation = {}, { shortTitles = false } = {}) {
  const labels = {};
  for (const group of ['timeDimensions', 'dimensions', 'measures', 'segments']) {
    for (const [key, meta] of Object.entries(annotation[group] || {})) {
      labels[key] = (shortTitles && meta.shortTitle) || meta.title || meta.shortTitle || key;
    }
  }
  const counts = {};
  for (const key of Object.keys(labels)) counts[labels[key]] = (counts[labels[key]] || 0) + 1;
  for (const key of Object.keys(labels)) if (counts[labels[key]] > 1) labels[key] = key;
  return labels;
}

export function rowsToDisplay(data, annotation = {}, options = {}) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const labels = displayLabels(annotation, options);
  const numeric = new Set();
  const timeKeys = timeKeysOf(annotation);
  for (const group of ['timeDimensions', 'dimensions', 'measures', 'segments']) {
    for (const [key, meta] of Object.entries(annotation[group] || {})) {
      if (group === 'measures' || meta.type === 'number') numeric.add(key);
    }
  }

  // With a granularity Cube returns both `cube.date` and `cube.date.month`; show only the bucket.
  const bucketed = new Set(Object.keys(data[0]).filter((k) => granularityOf(k)).map((k) => k.slice(0, k.lastIndexOf('.'))));

  return data.map((row) => {
    const out = {};
    for (const [key, value] of Object.entries(row)) {
      if (bucketed.has(key)) continue;
      const label = labels[key] || key;
      if (numeric.has(key) && value !== null && value !== '' && !Number.isNaN(Number(value))) {
        out[label] = Number(value);
      } else if (timeKeys.has(key) || granularityOf(key)) {
        out[label] = formatTimeBucket(value, timeKeys.get(key) || granularityOf(key));
      } else {
        out[label] = value;
      }
    }
    return out;
  });
}

/** Requirements per display type, evaluated against the form. */
export function displayRequirements(form) {
  const measures = form.measures?.length || 0;
  const dims = (form.dimensions?.length || 0) + (form.timeDimension && form.granularity ? 1 : 0);
  switch (form.displayType) {
    case 'stat':
      return [
        { ok: measures === 1, text: 'Exactly one measure', hint: 'The stat card shows a single number' },
        { ok: dims === 0, text: 'No dimensions or time breakdown', hint: 'Use a date range filter instead of a breakdown' },
      ];
    case 'combo':
      return [
        { ok: dims === 1, text: 'One dimension or time breakdown', hint: 'Becomes the categories along the bottom' },
        { ok: measures >= 2, text: 'At least two values', hint: 'Show some as bars and others as lines' },
      ];
    case 'bar':
    case 'pie':
      return [
        { ok: dims >= 1, text: 'At least one dimension (or a time breakdown)', hint: 'Becomes the categories' },
        { ok: measures >= 1, text: 'At least one measure', hint: 'Becomes the bar height / slice size' },
      ];
    case 'line':
    case 'area':
      return [
        { ok: dims >= 1, text: 'A time breakdown or an ordered dimension', hint: 'Pick a time dimension with a granularity for a trend' },
        { ok: measures >= 1, text: 'At least one measure', hint: 'Becomes the line' },
      ];
    case 'pivot':
      return [
        { ok: measures >= 1, text: 'At least one measure', hint: 'Fills the cells' },
        { ok: dims >= 1, text: 'At least one dimension (or time breakdown)', hint: 'Choose which go down the side and which across the top' },
      ];
    default:
      return [];
  }
}

/** Builder form fields that live in a saved report's display config. */
export function displayToForm(display = {}, visibility = 'company') {
  const pivot = display.pivot || {};
  return {
    compare: display.compare || '',
    totalRow: Boolean(display.totalRow),
    topN: display.topN ? { ...display.topN } : null,
    calculations: (display.calculations || []).map((c) => ({ ...c })),
    colorRules: (display.colorRules || []).map((r) => ({ ...r })),
    target: display.target === null || display.target === undefined ? '' : String(display.target),
    inheritDateRange: display.inheritDateRange !== false,
    visibility,
    pivotRows: pivot.rows || [],
    pivotColumns: pivot.columns || [],
    pivotMeasure: pivot.measure || '',
    pivotTotalColumn: pivot.totalColumn !== false,
    orientation: display.orientation === 'horizontal' ? 'horizontal' : 'vertical',
    stacking: display.stacking || 'auto',
    lineSeries: Array.isArray(display.lineSeries) ? [...display.lineSeries] : null,
    pieStyle: display.pieStyle === 'pie' ? 'pie' : 'donut',
    pieSlices: display.pieSlices || 8,
  };
}

/**
 * The chart options a display type uses, as saved: sideways bars and stacking
 * for bar charts, stacking for area charts, which values are lines on a
 * combined chart, and the style and number of slices of a pie. Defaults are
 * left out, so a report saves only what was chosen.
 */
export function chartOptions(form) {
  const t = form.displayType;
  const out = {};
  if (t === 'bar' && form.orientation === 'horizontal') out.orientation = 'horizontal';
  if ((t === 'bar' || t === 'area') && form.stacking && form.stacking !== 'auto') out.stacking = form.stacking;
  if (t === 'combo' && Array.isArray(form.lineSeries)) out.lineSeries = form.lineSeries.filter((m) => (form.measures || []).includes(m) || String(m).startsWith('calc:'));
  if (t === 'pie' && form.pieStyle === 'pie') out.pieStyle = 'pie';
  if (t === 'pie' && Number(form.pieSlices) && Number(form.pieSlices) !== 8) out.pieSlices = Number(form.pieSlices);
  return out;
}

/** Saved-report payload from the builder form. */
export function formToReport(form) {
  const displayType = form.displayType || 'table';
  const display = {
    displayType,
    compare: form.compare || null,
    totalRow: Boolean(form.totalRow),
    topN: form.topN && form.topN.dimension ? { ...form.topN, count: Number(form.topN.count) || 10 } : null,
    calculations: form.calculations || [],
    // Rules still being typed (no number yet) are left out until they are complete.
    colorRules: (form.colorRules || [])
      .filter((r) => r.kind === 'scale' || (r.value !== '' && Number.isFinite(Number(r.value)) && (r.operator !== 'between' || (r.value2 !== '' && Number.isFinite(Number(r.value2))))))
      .map((r) => (r.kind === 'scale' ? r : { ...r, value: Number(r.value), ...(r.operator === 'between' ? { value2: Number(r.value2) } : {}) })),
    target: form.target === '' || form.target === null || form.target === undefined ? null : Number(form.target),
    inheritDateRange: form.inheritDateRange !== false,
    pivot: displayType === 'pivot'
      ? {
        rows: form.pivotRows || [],
        columns: form.pivotColumns || [],
        measure: form.pivotMeasure || form.measures?.[0] || null,
        // Saved only when turned off, so existing pivots keep their total column.
        ...(form.pivotTotalColumn === false ? { totalColumn: false } : {}),
      }
      : null,
    ...chartOptions(form),
  };
  return {
    name: String(form.title || '').trim(),
    cubeQuery: buildCubeQuery(form),
    display,
    refreshMinutes: Number(form.refreshMinutes) || EMPTY_FORM.refreshMinutes,
    visibility: form.visibility || 'company',
  };
}
